/**
 * RF Cart Sync
 * Synchronizes option products with their parent products in cart.
 * 
 * Option products have hidden properties:
 *   _rf_parent: variant ID of the main product
 *   _rf_parent_key: line item key of the main product
 *   _rf_option_title: display name of the option
 * 
 * Main products have hidden properties:
 *   _Option Products: comma-separated list of option details
 *   _Options Total Price: total price of options
 */

(function() {
  'use strict';

  // Timing constants — Shopify's cart API is eventually consistent.
  // After a cart mutation, section rendering may still reflect old state.
  // These delays are empirically tuned to let Shopify's backend propagate changes.
  const DELAY_CART_RELOAD = 300;    // ms — after passthrough cart ops, reload cart data
  const DELAY_CART_ADD_RELOAD = 500; // ms — after add-to-cart (slower due to inventory checks)
  const DELAY_SECTION_REFETCH = 800; // ms — before re-fetching rendered sections (needs full propagation)
  const DELAY_ORPHAN_CLEANUP = 300;  // ms — before checking for orphaned fees after item removal

  const RF_CART_SYNC = {
    debug: false,
    cartData: null,
    mainItemKeys: {},    // { mainItemKey: [optionItemKey1, optionItemKey2, ...] }
    mainItemVariants: {}, // { mainVariantId: [mainItemKey1, mainItemKey2, ...] }
    isSyncing: false,
    
    log(...args) {
      if (this.debug) console.log('[RF Cart Sync]', ...args);
    },

    /**
     * Initialize
     */
    init() {
      this.log('Initializing...');
      this.interceptFetch();
      this.loadCart();
    },

    /**
     * Load cart data and build maps
     */
    async loadCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.cartData = cart;
        this.log('Cart loaded:', cart.items.length, 'items');
        this.buildMaps(cart);
      } catch (error) {
        console.error('[RF Cart Sync] Error loading cart:', error);
      }
    },

    /**
     * Build parent-child relationship maps from cart data
     * IMPORTANT: Uses _rf_parent_key for precise matching when available,
     * falls back to variant_id matching for backwards compatibility
     */
    buildMaps(cart) {
      this.mainItemKeys = {};
      this.mainItemVariants = {};

      const optionItems = [];
      const mainItems = [];

      this.log('=== Building Maps ===');
      this.log('Total cart items:', cart.items.length);

      const oneTimeFeeItems = [];
      
      cart.items.forEach((item, index) => {
        this.log(`Item ${index}:`, {
          key: item.key,
          variant_id: item.variant_id,
          product_id: item.product_id,
          title: item.title?.substring(0, 40),
          has_rf_parent: !!item.properties?.['_rf_parent'],
          has_option_products: !!item.properties?.['_Option Products'],
          is_one_time_fee: item.properties?.['_rf_one_time_fee'] === 'true',
          _rf_parent: item.properties?.['_rf_parent'],
          _rf_parent_key: item.properties?.['_rf_parent_key'],
          _rf_main_product_id: item.properties?.['_rf_main_product_id']
        });

        if (item.properties && item.properties['_rf_one_time_fee'] === 'true') {
          // One-time fee item (visible in cart, not synced with quantity)
          oneTimeFeeItems.push(item);
        } else if (item.properties && item.properties['_rf_parent']) {
          // Hidden option item (synced with main product quantity)
          optionItems.push(item);
        } else {
          // Main product item — any item that is not an option or fee
          // Previously checked for '_Option Products' but that can be missing
          // after Shopify re-keys items during discount apply
          mainItems.push(item);
        }
      });
      
      this.log('Found one-time fee items:', oneTimeFeeItems.length);

      this.log('Found main items:', mainItems.length);
      this.log('Found option items:', optionItems.length);

      // Build mainItemKey -> [optionKeys] map
      // Use _rf_parent_key for precise matching when available, with fallback to variant_id
      mainItems.forEach(mainItem => {
        const mainVariantId = String(mainItem.variant_id);
        const mainItemKey = mainItem.key;
        
        if (!this.mainItemVariants[mainVariantId]) {
          this.mainItemVariants[mainVariantId] = [];
        }
        this.mainItemVariants[mainVariantId].push(mainItemKey);
        this.mainItemKeys[mainItemKey] = [];

        optionItems.forEach(optItem => {
          const optParentKey = optItem.properties['_rf_parent_key'];
          const optParentVariant = String(optItem.properties['_rf_parent']);
          
          // Prefer matching by _rf_parent_key (precise), fall back to variant_id
          let isMatch = false;
          if (optParentKey) {
            // First try exact key match
            if (optParentKey === mainItemKey) {
              isMatch = true;
            } else {
              // Check if the _rf_parent_key's variant matches this main item's variant
              // This handles the case where the key changed after file upload
              const optParentKeyVariant = optParentKey.split(':')[0];
              if (optParentKeyVariant === mainVariantId) {
                // Check if there's no other main item with this exact key
                const exactKeyExists = mainItems.some(m => m.key === optParentKey);
                if (!exactKeyExists) {
                  this.log(`  Fallback match: option parent key ${optParentKey} -> main ${mainItemKey} (by variant ${mainVariantId})`);
                  isMatch = true;
                }
              }
            }
          } else {
            // No parent key - match by variant ID (legacy)
            isMatch = optParentVariant === mainVariantId;
          }
          
          if (isMatch) {
            this.mainItemKeys[mainItemKey].push(optItem.key);
            this.log(`  Linked option "${optItem.title?.substring(0, 30)}" to main "${mainItem.title?.substring(0, 30)}"`);
          }
        });
      });

      this.log('=== Maps Built ===');
      this.log('Main items mapping:', Object.keys(this.mainItemKeys).length);
      for (const [mainKey, optKeys] of Object.entries(this.mainItemKeys)) {
        this.log(`  ${mainKey}: ${optKeys.length} options`);
      }
      const unlinkedOptions = optionItems.filter(o => {
        const linked = Object.values(this.mainItemKeys).flat().includes(o.key);
        return !linked;
      });
      this.log('Unlinked option items:', unlinkedOptions.map(o => ({
        key: o.key,
        title: o.title?.substring(0, 30),
        _rf_parent: o.properties['_rf_parent'],
        _rf_parent_key: o.properties['_rf_parent_key']
      })));
    },

    /**
     * Find option keys for a specific main item by its key.
     * After cart reload, the main item key may have changed (Shopify re-keys on property changes).
     * Strategy:
     *   1. Exact key match in mainItemKeys (best case — key didn't change)
     *   2. If key changed, find by _rf_parent_key match in fresh cart data
     *   3. Last resort: variant_id match (only if single main item for that variant)
     */
    findOptionKeysForMainItem(originalMainKey) {
      // Strategy 1: Exact key match in rebuilt maps
      if (this.mainItemKeys[originalMainKey] && this.mainItemKeys[originalMainKey].length > 0) {
        this.log('findOptionKeysForMainItem: exact key match for', originalMainKey);
        return this.mainItemKeys[originalMainKey];
      }

      if (!this.cartData || !this.cartData.items) return [];

      // Strategy 2: Find options whose _rf_parent_key matches the original key
      const optionKeys = [];
      this.cartData.items.forEach(item => {
        if (item.properties && item.properties['_rf_parent_key'] === originalMainKey) {
          optionKeys.push(item.key);
        }
      });
      if (optionKeys.length > 0) {
        this.log('findOptionKeysForMainItem: matched by _rf_parent_key for', originalMainKey, ':', optionKeys);
        return optionKeys;
      }

      // Strategy 3: Fallback to variant_id — only safe if single main item for this variant
      const variantId = originalMainKey.split(':')[0];
      const mainKeysForVariant = this.mainItemVariants[variantId];
      if (mainKeysForVariant && mainKeysForVariant.length === 1) {
        // Only one main item with this variant — safe to use its options
        const singleMainKey = mainKeysForVariant[0];
        if (this.mainItemKeys[singleMainKey]) {
          this.log('findOptionKeysForMainItem: single variant fallback for', originalMainKey, '->', singleMainKey);
          return this.mainItemKeys[singleMainKey];
        }
      }

      // If multiple main items share this variant, use variant_id match from cart data
      // but ONLY for options that don't have _rf_parent_key (legacy items)
      const legacyOptionKeys = [];
      this.cartData.items.forEach(item => {
        if (item.properties && item.properties['_rf_parent']) {
          const parentVariant = String(item.properties['_rf_parent']);
          if (parentVariant === variantId && !item.properties['_rf_parent_key']) {
            legacyOptionKeys.push(item.key);
          }
        }
      });
      if (legacyOptionKeys.length > 0) {
        this.log('findOptionKeysForMainItem: legacy variant_id fallback for', originalMainKey, ':', legacyOptionKeys);
      }
      return legacyOptionKeys;
    },

    /**
     * Re-fetch Shopify sections and merge fresh data into responseData.
     * Used after cart sync operations to ensure UI reflects the latest state.
     * @param {Object} parsedBody - Original request body (contains sections, sections_url)
     * @param {Object} responseData - Response data object to merge sections into
     * @param {Function} fetchFn - Original (un-patched) fetch function
     * @returns {Object} responseData with fresh sections merged in
     */
    async refetchSections(parsedBody, responseData, fetchFn) {
      const sectionsParam = parsedBody.sections;
      if (!sectionsParam || sectionsParam.length === 0) return responseData;

      // Brief delay to let Shopify's section rendering catch up with cart state
      await new Promise(resolve => setTimeout(resolve, DELAY_SECTION_REFETCH));

      const sectionsUrl = parsedBody.sections_url || window.location.pathname;
      const sectionsQuery = Array.isArray(sectionsParam) ? sectionsParam.join(',') : sectionsParam;
      const freshSectionsResponse = await fetchFn(`${sectionsUrl}?sections=${sectionsQuery}`);
      const freshSections = await freshSectionsResponse.json();

      responseData.sections = freshSections;
      if (this.cartData) {
        responseData.item_count = this.cartData.item_count;
        responseData.items = this.cartData.items;
        responseData.total_price = this.cartData.total_price;
        responseData.items_subtotal_price = this.cartData.items_subtotal_price;
      }
      return responseData;
    },

    /**
     * Intercept fetch API to catch cart/change.js calls
     */
    interceptFetch() {
      const self = this;
      const originalFetch = window.fetch;

      window.fetch = async function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const options = args[1] || {};
        
        // Helper: pass through to original fetch, then reload cart in background
        const passthrough = async (delayMs = DELAY_CART_RELOAD) => {
          const resp = await originalFetch.apply(this, args);
          setTimeout(() => self.loadCartWithFetch(originalFetch), delayMs);
          return resp;
        };

        // Intercept cart/change.js calls (quantity changes and removals)
        if (url.includes('/cart/change.js') && !self.isSyncing) {
          
          // Skip sync during file upload or parent key fixing
          if (window.RF_FILE_UPLOADING || window.RF_FIXING_PARENT_KEYS) {
            self.log('Skipping sync - file upload or fix in progress (no reload)');
            return originalFetch.apply(this, args); // pure passthrough, no side effects
          }
          
          // Parse request body
          let parsedBody = null;
          try {
            const body = options.body;
            if (typeof body === 'string') {
              parsedBody = JSON.parse(body);
            } else if (body instanceof FormData) {
              parsedBody = {};
              for (const [key, value] of body.entries()) {
                parsedBody[key] = value;
              }
            }
          } catch (e) {
            self.log('Error parsing body:', e);
          }
          
          // If we can't parse body, just pass through
          if (!parsedBody) {
            return passthrough();
          }
          
          let changedKey = parsedBody.id;
          const newQuantity = parseInt(parsedBody.quantity);
          const line = parsedBody.line;
          
          // If using line index (cart page), resolve to item key
          if (line !== undefined && !changedKey && self.cartData) {
            const lineIndex = parseInt(line) - 1;
            if (self.cartData.items[lineIndex]) {
              changedKey = self.cartData.items[lineIndex].key;
              self.log('Resolved line', line, 'to key:', changedKey);
            }
          }
          
          self.log('Cart change:', { changedKey, newQuantity });
          
          // Check if the changed item is a main product with hidden options (pre-change snapshot)
          const staleOptionKeys = changedKey ? self.mainItemKeys[changedKey] : null;
          const hasHiddenOptions = staleOptionKeys && staleOptionKeys.length > 0;
          
          // Check if there are one-time fees in cart
          const hasOneTimeFees = self.cartData?.items?.some(item => 
            item.properties?.['_rf_one_time_fee'] === 'true'
          );
          
          // CASE 1: Main product WITH hidden options - full sync flow
          if (hasHiddenOptions) {
            try {
              self.log('Main product with options detected. Stale option keys:', staleOptionKeys);

              // Pre-step: If removing (qty=0), collect ALL option keys for this variant
              // BEFORE executing the change — Shopify re-keying can break key lookups after removal
              let allOptionKeysForVariant = [...staleOptionKeys];
              if (newQuantity === 0 && self.cartData?.items) {
                const changedItem = self.cartData.items.find(i => i.key === changedKey);
                if (changedItem) {
                  const variantId = String(changedItem.variant_id);
                  self.cartData.items.forEach(item => {
                    if (item.properties?.['_rf_parent'] &&
                        String(item.properties['_rf_parent']) === variantId &&
                        allOptionKeysForVariant.indexOf(item.key) === -1) {
                      allOptionKeysForVariant.push(item.key);
                      self.log('Pre-collected extra option key by variant:', item.key);
                    }
                  });
                }
                self.log('All option keys to remove (pre-collected):', allOptionKeysForVariant);
              }

              // Step 1: Execute the original cart/change.js call
              const originalResponse = await originalFetch.apply(this, args);
              const originalData = await originalResponse.clone().json();
              
              if (originalData.status && originalData.status !== 200) {
                self.log('Original request failed, returning as-is');
                return originalResponse;
              }
              
              // Step 2: Reload cart to get fresh keys
              await self.loadCartWithFetch(originalFetch);
              
              // Step 3: Sync option quantities using precise key-based matching
              // For removal (qty=0), use the pre-collected keys to avoid missing orphans
              const freshOptionKeys = newQuantity === 0
                ? allOptionKeysForVariant.filter(k => self.cartData?.items?.some(i => i.key === k))
                : self.findOptionKeysForMainItem(changedKey);
              self.log('Fresh option keys for main item', changedKey, ':', freshOptionKeys);
              const syncedCart = await self.syncOptionQuantities(freshOptionKeys, newQuantity, originalFetch);
              
              if (syncedCart) {
                self.cartData = syncedCart;
                self.buildMaps(syncedCart);
              }

              // Reload cart to get accurate item_count after all sync operations
              // (Shopify's /cart/update.js response may have stale item_count during discount re-keying)
              await self.loadCartWithFetch(originalFetch);
              
              // Step 4: Fix re-keyed parent links after discount change
              window.RF_FIXING_PARENT_KEYS = true;
              try {
                await self.fixBrokenParentKeys(originalFetch);
              } finally {
                window.RF_FIXING_PARENT_KEYS = false;
              }

              // Step 5: Clean up orphaned one-time fees if product was removed
              if (newQuantity === 0 && hasOneTimeFees) {
                await self.cleanupOrphanedOneTimeFees(originalFetch);
              }
              
              // If cart is empty and on cart page, reload
              if (self.cartData && self.cartData.item_count === 0 && window.location.pathname.includes('/cart')) {
                window.location.reload();
                return new Response(JSON.stringify(originalData), {
                  status: 200, statusText: 'OK',
                  headers: { 'Content-Type': 'application/json' }
                });
              }
              
              // Step 6: Re-fetch sections with fresh cart state
              const updatedData = await self.refetchSections(parsedBody, originalData, originalFetch);
              return new Response(JSON.stringify(updatedData), {
                status: 200, statusText: 'OK',
                headers: { 'Content-Type': 'application/json' }
              });
              
            } catch (error) {
              self.log('Error in options sync:', error);
              // On error, pass through normally
              setTimeout(() => self.loadCartWithFetch(originalFetch), DELAY_CART_RELOAD);
              return originalFetch.apply(this, args);
            }
          }
          
          // CASE 2: Item removed AND one-time fees exist - cleanup needed
          if (newQuantity === 0 && hasOneTimeFees) {
            self.log('Item removed & one-time fees exist - running cleanup...');
            
            // Execute the original call OUTSIDE try/catch — it must happen exactly once
            const response = await originalFetch.apply(this, args);
            const responseData = await response.clone().json();
            
            try {
              // Wait for Shopify, then cleanup (this is what can fail)
              await new Promise(resolve => setTimeout(resolve, DELAY_ORPHAN_CLEANUP));
              await self.cleanupOrphanedOneTimeFees(originalFetch);
              await self.loadCartWithFetch(originalFetch);
              
              // If cart is empty and on cart page, reload
              if (self.cartData && self.cartData.item_count === 0 && window.location.pathname.includes('/cart')) {
                window.location.reload();
                return response;
              }
              
              // Re-fetch sections for fresh UI
              const updatedData = await self.refetchSections(parsedBody, responseData, originalFetch);
              return new Response(JSON.stringify(updatedData), {
                status: 200, statusText: 'OK',
                headers: { 'Content-Type': 'application/json' }
              });
              
            } catch (e) {
              self.log('Error in one-time fee cleanup:', e);
              // Cleanup failed, but original cart/change.js already succeeded — return it
              return response;
            }
          }
          
          // CASE 3: Normal item, no sync needed - pass through
          return passthrough();
        }

        // Intercept cart/update.js - just reload cart data after
        if (url.includes('/cart/update.js') && !self.isSyncing) {
          // Skip reload during file upload or parent key fixing
          if (window.RF_FILE_UPLOADING || window.RF_FIXING_PARENT_KEYS) {
            self.log('Skipping cart/update.js reload - file upload or fix in progress');
            return originalFetch.apply(this, args);
          }
          return passthrough();
        }

        // Intercept cart/add.js - reload cart data after, then fix re-keyed parent links
        if (url.includes('/cart/add.js')) {
          // Skip reload during file upload or parent key fixing
          if (window.RF_FILE_UPLOADING || window.RF_FIXING_PARENT_KEYS) {
            self.log('Skipping cart/add.js reload - file upload or fix in progress');
            return originalFetch.apply(this, args);
          }

          // Execute original request first
          const addResp = await originalFetch.apply(this, args);

          // After delay, reload cart and fix any broken _rf_parent_key links
          // (Shopify re-keys items when automatic discounts are applied)
          setTimeout(async () => {
            window.RF_FIXING_PARENT_KEYS = true;
            try {
              await self.loadCartWithFetch(originalFetch);
              await self.fixBrokenParentKeys(originalFetch);
            } finally {
              window.RF_FIXING_PARENT_KEYS = false;
              await self.loadCartWithFetch(originalFetch);
              const cartNotification = document.querySelector('cart-notification');
              if (cartNotification) {
                fetch(`${window.Shopify.routes.root}?sections=cart-notification-content`)
                  .then(r => r.json())
                  .then(data => cartNotification.updateContent(data))
                  .catch(() => {});
              }
            }
          }, DELAY_CART_ADD_RELOAD);

          return addResp;
        }

        return originalFetch.apply(this, args);
      };

      this.originalFetch = originalFetch;
    },

    /**
     * Fix broken _rf_parent_key links after Shopify re-keys items.
     * Shopify re-keys cart items when automatic discounts are applied (e.g. bulk discount at 10 qty).
     * When this happens, option items still store the OLD key in _rf_parent_key,
     * making them orphaned ghost items.
     *
     * Strategy:
     *   1. Find option items whose _rf_parent_key doesn't match any current main item key
     *   2. For each orphaned option, find the correct main item by variant_id match
     *   3. Re-add the option with the updated _rf_parent_key, remove the stale one
     */
    async fixBrokenParentKeys(fetchFn) {
      if (!this.cartData || !this.cartData.items) return;

      const fn = fetchFn || this.originalFetch || fetch;
      const items = this.cartData.items;

      // Build current main item key set: { variantId: [key1, key2, ...] }
      const mainKeysByVariant = {};
      const allMainKeys = new Set();

      items.forEach(item => {
        if (item.properties?.['_rf_parent']) return;       // skip option items
        if (item.properties?.['_rf_one_time_fee'] === 'true') return; // skip fees
        if (!item.properties?.['_Option Products']) return; // skip non-RF items

        const vid = String(item.variant_id);
        if (!mainKeysByVariant[vid]) mainKeysByVariant[vid] = [];
        mainKeysByVariant[vid].push(item.key);
        allMainKeys.add(item.key);
      });

      // Find orphaned option items (have _rf_parent_key that no longer exists)
      const orphanedOptions = items.filter(item => {
        if (!item.properties?.['_rf_parent']) return false; // must be option item
        const storedParentKey = item.properties['_rf_parent_key'];
        if (!storedParentKey) return false; // legacy item, skip
        return !allMainKeys.has(storedParentKey); // key no longer exists → orphaned
      });

      // Also fix mismatched qty: option qty > main item qty (caused by Shopify merging options)
      await this.fixMismatchedOptionQty(fn, items, allMainKeys);

      if (orphanedOptions.length === 0) {
        this.log('fixBrokenParentKeys: no orphaned options found');
        return;
      }

      this.log('fixBrokenParentKeys: found', orphanedOptions.length, 'orphaned option items - fixing...');

      // For each orphaned option, find correct parent by variant_id
      const toAdd = [];
      const toRemove = [];

      orphanedOptions.forEach(optItem => {
        const parentVariantId = String(optItem.properties['_rf_parent']);
        const candidateKeys = mainKeysByVariant[parentVariantId];

        if (!candidateKeys || candidateKeys.length === 0) {
          // No main item with this variant — truly orphaned, leave for cleanup
          this.log('  No main item found for variant', parentVariantId, '- skipping');
          return;
        }

        if (candidateKeys.length === 1) {
          // Exactly one main item with this variant — safe to re-link
          const newParentKey = candidateKeys[0];
          this.log('  Re-linking option', optItem.title?.substring(0, 30), 'from', optItem.properties['_rf_parent_key'], '->', newParentKey);
          toAdd.push({
            id: optItem.variant_id,
            quantity: optItem.quantity,
            properties: {
              ...optItem.properties,
              '_rf_parent_key': newParentKey
            }
          });
          toRemove.push(optItem.key);
        } else {
          // Multiple main items with same variant_id
          // Strategy: match by old key's variant prefix to find the correct new key
          const oldParentKey = optItem.properties['_rf_parent_key'] || '';
          const oldVariantPart = oldParentKey.split(':')[0];
          // Find the new key that starts with the same variant part
          const matchedKey = candidateKeys.find(k => k.startsWith(oldVariantPart + ':'));
          const newParentKey = matchedKey || candidateKeys[0];
          this.log('  Multiple main items for variant', parentVariantId, '- matched key:', newParentKey, '(from old key:', oldParentKey, ')');
          toAdd.push({
            id: optItem.variant_id,
            quantity: optItem.quantity,
            properties: {
              ...optItem.properties,
              '_rf_parent_key': newParentKey
            }
          });
          toRemove.push(optItem.key);
        }
      });

      if (toRemove.length === 0) return;

      try {
        // Remove orphaned option items
        const removeUpdates = {};
        toRemove.forEach(key => removeUpdates[key] = 0);
        await fn('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: removeUpdates })
        });

        // Re-add with correct parent key
        if (toAdd.length > 0) {
          await fn('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: toAdd })
          });
        }

        // Reload maps with fresh data
        await this.loadCartWithFetch(fn);
        this.log('fixBrokenParentKeys: done, re-linked', toAdd.length, 'options');
      } catch (e) {
        this.log('fixBrokenParentKeys: error:', e);
      }
    },

    /**
     * Fix mismatched option quantities.
     * When Shopify merges option items (same variant_id + same _rf_parent_key),
     * the option qty becomes > main item qty. We need to split them correctly.
     */
    async fixMismatchedOptionQty(fn, items, allMainKeys) {
      // Build main item qty map: { key: qty }
      const mainItemQty = {};
      items.forEach(item => {
        if (item.properties?.['_rf_parent']) return;
        if (item.properties?.['_rf_one_time_fee'] === 'true') return;
        mainItemQty[item.key] = item.quantity;
      });

      // Find option items where qty > parent main item qty
      const mismatchedOptions = items.filter(item => {
        if (!item.properties?.['_rf_parent']) return false;
        const parentKey = item.properties['_rf_parent_key'];
        if (!parentKey || !allMainKeys.has(parentKey)) return false;
        const parentQty = mainItemQty[parentKey] || 0;
        return item.quantity > parentQty;
      });

      if (mismatchedOptions.length === 0) return;

      this.log('fixMismatchedOptionQty: found', mismatchedOptions.length, 'mismatched option items');

      // For each mismatched option, find all main items with same variant_id
      // and split the option qty across them
      const toAdd = [];
      const toUpdate = {};

      for (const optItem of mismatchedOptions) {
        const parentKey = optItem.properties['_rf_parent_key'];
        const parentVariantId = String(optItem.properties['_rf_parent']);
        const correctQty = mainItemQty[parentKey] || 0;
        const excessQty = optItem.quantity - correctQty;

        if (excessQty <= 0) continue;

        // Find other main items with same variant_id that don't have this option linked
        const otherMainItems = items.filter(i =>
          String(i.variant_id) === parentVariantId &&
          !i.properties?.['_rf_parent'] &&
          i.properties?.['_rf_one_time_fee'] !== 'true' &&
          i.key !== parentKey
        );

        if (otherMainItems.length === 0) continue;

        this.log('  Option', optItem.title?.substring(0, 30), 'qty:', optItem.quantity, '-> correct qty:', correctQty, '| excess:', excessQty, '| other mains:', otherMainItems.length);

        // Fix current option to correct qty
        toUpdate[optItem.key] = correctQty;

        // Add separate option items for other main items
        let remainingExcess = excessQty;
        for (const mainItem of otherMainItems) {
          const qtyForThis = Math.min(mainItem.quantity, remainingExcess);
          if (qtyForThis <= 0) continue;

          toAdd.push({
            id: optItem.variant_id,
            quantity: qtyForThis,
            properties: {
              ...optItem.properties,
              '_rf_parent_key': mainItem.key,
              '_rf_parent': String(mainItem.variant_id)
            }
          });
          remainingExcess -= qtyForThis;
          if (remainingExcess <= 0) break;
        }
      }

      if (Object.keys(toUpdate).length === 0 && toAdd.length === 0) return;

      try {
        // First fix quantities of existing options
        if (Object.keys(toUpdate).length > 0) {
          await fn('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: toUpdate })
          });
        }

        // Then add new option items for other main items
        if (toAdd.length > 0) {
          await fn('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: toAdd })
          });
        }

        await this.loadCartWithFetch(fn);
        this.log('fixMismatchedOptionQty: done');
      } catch (e) {
        this.log('fixMismatchedOptionQty: error:', e);
      }
    },

    /**
     * Load cart using original fetch
     */
    async loadCartWithFetch(fetchFn) {
      try {
        const response = await fetchFn('/cart.js');
        const cart = await response.json();
        this.cartData = cart;
        this.buildMaps(cart);
        this.log('Cart reloaded:', cart.items.length, 'items');
        // Dispatch event for other RF components (bundle discount, etc.)
        document.dispatchEvent(new CustomEvent('rf:cart:updated', { detail: { cart: cart } }));
      } catch (error) {
        console.error('[RF Cart Sync] Error reloading cart:', error);
      }
    },

    /**
     * Clean up orphaned one-time fee items
     * Called when a product is removed - checks if any items from that product remain
     * If not, removes the one-time fee for that product
     */
    async cleanupOrphanedOneTimeFees(fetchFn) {
      try {
        const fn = fetchFn || this.originalFetch || fetch;
        
        // Get fresh cart data
        const cartResponse = await fn('/cart.js');
        const cart = await cartResponse.json();
        
        // Find all one-time fee items
        const oneTimeFeeItems = cart.items.filter(item => 
          item.properties?.['_rf_one_time_fee'] === 'true'
        );
        
        if (oneTimeFeeItems.length === 0) {
          this.log('No one-time fee items to check');
          return;
        }
        
        this.log('Checking', oneTimeFeeItems.length, 'one-time fee items for orphans');
        
        // Get all unique product IDs that have one-time fees
        const productIdsWithFees = [...new Set(oneTimeFeeItems.map(item => 
          item.properties?.['_rf_main_product_id']
        ).filter(Boolean))];
        
        // For each product ID, check if there are still items from that product in cart
        const orphanedFeeKeys = [];
        
        for (const productId of productIdsWithFees) {
          // Check if any items from this product exist (excluding the one-time fee itself)
          const productItemsExist = cart.items.some(item => {
            // Skip the one-time fee items themselves
            if (item.properties?.['_rf_one_time_fee'] === 'true') return false;
            // Skip hidden option items (they follow main product)
            if (item.properties?.['_rf_parent']) return false;
            
            // Check if this item belongs to the product by product_id
            const itemProductId = item.product_id?.toString();
            const matches = itemProductId === productId;
            if (matches) {
              this.log('  Found item from product', productId, ':', item.title?.substring(0, 30));
            }
            return matches;
          });
          
          if (!productItemsExist) {
            // No items from this product - find and mark the fee for removal
            const feeItems = oneTimeFeeItems.filter(item => 
              item.properties?.['_rf_main_product_id'] === productId
            );
            feeItems.forEach(item => {
              this.log('  Marking for removal:', item.title);
              orphanedFeeKeys.push(item.key);
            });
            this.log('Product', productId, 'has no items left - will remove one-time fee');
          } else {
            this.log('Product', productId, 'still has items - keeping one-time fee');
          }
        }
        
        // Remove orphaned fees
        if (orphanedFeeKeys.length > 0) {
          this.log('Removing orphaned one-time fees:', orphanedFeeKeys);
          const updates = {};
          orphanedFeeKeys.forEach(key => updates[key] = 0);
          
          await fn('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
          
          // Reload cart
          await this.loadCartWithFetch(fn);
        }
        
      } catch (error) {
        console.error('[RF Cart Sync] Error cleaning up one-time fees:', error);
      }
    },

    /**
     * Sync option product quantities and return the updated cart
     * One-time fee options keep quantity=1 regardless of main product quantity
     */
    async syncOptionQuantities(optionKeys, newQuantity, fetchFn) {
      if (!optionKeys || optionKeys.length === 0) {
        this.log('No option keys to sync');
        return null;
      }

      this.isSyncing = true;
      this.log('Syncing', optionKeys.length, 'options to quantity:', newQuantity);

      try {
        const updates = {};
        
        // Check each option - if it's a one-time fee, keep quantity at 1
        optionKeys.forEach(key => {
          // Find the item in cartData to check for one-time fee property
          const item = this.cartData?.items?.find(i => i.key === key);
          const isOneTimeFee = item?.properties?.['_rf_one_time_fee'] === 'true';
          
          if (isOneTimeFee) {
            // One-time fee: always quantity 1
            updates[key] = 1;
            this.log(`  Option "${item?.title?.substring(0, 30)}" is one-time fee - keeping qty=1`);
          } else {
            // Regular option: sync with main product quantity
            updates[key] = newQuantity;
          }
        });

        this.log('Update payload:', JSON.stringify(updates));

        const fn = fetchFn || this.originalFetch || fetch;
        const response = await fn('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        const result = await response.json();
        this.log('Sync response - item_count:', result.item_count);
        this.log('Sync response - items quantities:', result.items?.map(i => ({ 
          title: i.title?.substring(0, 30), 
          quantity: i.quantity,
          oneTimeFee: i.properties?.['_rf_one_time_fee']
        })));

        // Return the cart data from sync response
        return result;

      } catch (error) {
        console.error('[RF Cart Sync] Sync failed:', error);
        return null;
      } finally {
        this.isSyncing = false;
      }
    }
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => RF_CART_SYNC.init(), 100);
    });
  } else {
    setTimeout(() => RF_CART_SYNC.init(), 100);
  }

  window.RF_CART_SYNC = RF_CART_SYNC;
})();
